<template>
  <div class="onlyoffice-wrapper" :style="containerStyle">
    <div :id="containerId" class="onlyoffice-content" :style="{ width: '100%', height: '100%' }"></div>
  </div>
</template>

<script>
import request from '@/utils/request'
import { getUserId, getUserName } from '@/api/auth'
let isScriptLoaded = false

// 文件类型映射关系
const FILE_TYPE_MAP = {
  // 文本文档
  doc: 'word', docm: 'word', docx: 'word', dot: 'word', dotm: 'word', 
  dotx: 'word', epub: 'word', fb2: 'word', fodt: 'word', htm: 'word',
  html: 'word', mht: 'word', mhtml: 'word', odt: 'word', ott: 'word',
  pages: 'word', rtf: 'word', stw: 'word', sxw: 'word', txt: 'word',
  wps: 'word', wpt: 'word', xml: 'word',
  // 电子表格
  csv: 'cell', et: 'cell', ett: 'cell', fods: 'cell', numbers: 'cell',
  ods: 'cell', ots: 'cell', sxc: 'cell', xls: 'cell', xlsb: 'cell',
  xlsm: 'cell', xlsx: 'cell', xlt: 'cell', xltm: 'cell', xltx: 'cell',
  // 演示文稿
  dps: 'slide', dpt: 'slide', fodp: 'slide', key: 'slide', odp: 'slide',
  otp: 'slide', pot: 'slide', potm: 'slide', potx: 'slide', pps: 'slide',
  ppsm: 'slide', ppsx: 'slide', ppt: 'slide', pptm: 'slide', pptx: 'slide',
  sxi: 'slide',
  // PDF文档
  djvu: 'pdf', docxf: 'pdf', oform: 'pdf', oxps: 'pdf', pdf: 'pdf', xps: 'pdf'
}

export default {
  name: 'OnlyOffice',
  props: {
    fileUrl: { type: String, required: true },
    fileType: { type: String, default: 'docx' },
    title: { type: String, default: '文档' },
    documentType: { type: String, default: '' }, // 可以为空，由系统自动判断
    mode: { type: String, default: 'view' }, // 'view' | 'edit'
    editorConfig: { type: Object, default: () => ({}) },
    document: { type: Object, default: null },
    containerStyle: { type: [String, Object], default: 'width:100%;height:80vh;' },
    width: { type: String, default: '100%' },
    height: { type: String, default: '80vh' }
  },
  computed: {
    computedDocumentType() {
      // 如果指定了documentType，优先使用指定的
      if (this.documentType) {
        return this.documentType
      }
      // 获取文件扩展名（去掉点号并转小写）
      const ext = this.fileType.toLowerCase().replace(/^\./, '')
      // 根据扩展名获取文档类型，默认为word
      return FILE_TYPE_MAP[ext] || 'word'
    }
  },
  data() {
    return {
      onlyofficeUrl: '',
      docEditor: null,
      containerId: 'onlyoffice-container-' + Math.random().toString(36).substr(2, 8),
      tryInitTimer: null
    }
  },
  mounted() {
    this.fetchOnlyOfficeUrl()
  },
  watch: {
    fileUrl() { this.debouncedTryInitOnlyOffice() },
    mode() { this.debouncedTryInitOnlyOffice() },
    '$parent.onlyOfficeDialogVisible'(val) {
      if (!val) this.destroy()
    }
  },
  methods: {
    fetchOnlyOfficeUrl() {
      console.log('fetchOnlyOfficeUrl')
      request.get('/system/config').then(res => {
        this.onlyofficeUrl = res.data.onlyofficeUrl.replace(/\/$/, '')
        this.loadOnlyOfficeScript()
      })
    },
    loadOnlyOfficeScript() {
      console.log('loadOnlyOfficeScript')
      if (isScriptLoaded || window.DocsAPI) {
        isScriptLoaded = true
        this.tryInitOnlyOffice()
        return
      }
      const script = document.createElement('script')
      script.src = this.onlyofficeUrl + '/web-apps/apps/api/documents/api.js'
      script.onload = () => {
        isScriptLoaded = true
        this.tryInitOnlyOffice()
      }
      document.body.appendChild(script)
    },
    debouncedTryInitOnlyOffice() {
      if (this.tryInitTimer) clearTimeout(this.tryInitTimer)
      this.tryInitTimer = setTimeout(() => {
        this.tryInitOnlyOffice()
      }, 300)
    },
    parseWidthToPx(widthStr) {
      if (!widthStr) return '100%';
      if (widthStr.endsWith('px')) return parseInt(widthStr.replace('px', ''));
      if (widthStr.endsWith('vw')) {
        const vw = parseFloat(widthStr.replace('vw', ''));
        return Math.round(window.innerWidth * vw / 100) + 'px';
      }
      if (widthStr.endsWith('%')) {
        // 以视口宽度为基准
        const percent = parseFloat(widthStr.replace('%', ''));
        return Math.round(window.innerWidth * percent / 100) + 'px';
      }
      // 兜底
      return parseInt(widthStr) || 1000 + 'px';
    },
    parseHeightToPx(heightStr) {
      if (!heightStr) return '100%';
      if (heightStr.endsWith('px')) return parseInt(heightStr.replace('px', ''));
      if (heightStr.endsWith('vh')) {
        const vh = parseFloat(heightStr.replace('vh', ''));
        return Math.round(window.innerHeight * vh / 100) + 'px';
      }
      if (heightStr.endsWith('%')) {
        // 以视口高度为基准
        const percent = parseFloat(heightStr);
        return Math.round(window.innerHeight * percent / 100) + 'px';
      }
      // 兜底
      return parseInt(heightStr) || 800 + 'px';
    },
    tryInitOnlyOffice() {
      if (!isScriptLoaded || !window.DocsAPI || !this.fileUrl) return
      // 清空容器
      if (this.docEditor) this.docEditor.destroyEditor()
      const container = document.getElementById(this.containerId)
      if (container) container.innerHTML = ''
      // 生成唯一key，随机 
      const key = Math.random().toString(36).substring(2, 15)
      // 延迟渲染，避免首次渲染不完整
      setTimeout(() => {
        console.log("callbackUrl", this.editorConfig.callbackUrl)
        console.log('tryInitOnlyOffice, containerId, fileUrl, mode, fileType, documentType, editorConfig:', this.containerId, this.fileUrl, this.mode, this.fileType, this.computedDocumentType, this.editorConfig  )
        const docHeight = this.parseHeightToPx(this.height)
        const docWidth = this.parseWidthToPx(this.width)
        
        // 确保OnlyOffice获得正确的容器尺寸
        const containerElement = document.getElementById(this.containerId)
        let actualWidth = docWidth
        let actualHeight = docHeight
        
        if (containerElement) {
          const containerRect = containerElement.getBoundingClientRect()
          actualWidth = containerRect.width || docWidth
          actualHeight = containerRect.height || docHeight
          console.log('容器实际尺寸:', {
            width: containerRect.width,
            height: containerRect.height,
            actualWidth: actualWidth,
            actualHeight: actualHeight
          })
        }
        
        const documents = this.document || {}
        const mergedEditorConfig = Object.assign({
          mode: this.mode,
          lang: 'zh-CN',
          user: {
            id: getUserId(),
            name: getUserName()
          }
        }, this.editorConfig)
        
        this.docEditor = new window.DocsAPI.DocEditor(this.containerId, {
          document: Object.assign(documents, {
            fileType: this.fileType,
            key: key,
            title: this.title,
            url: this.fileUrl
          }),
          documentType: this.computedDocumentType,
          editorConfig: mergedEditorConfig,
          height: actualHeight,
          width: actualWidth
        })
      }, 1000)
    },
    refresh() {
      this.debouncedTryInitOnlyOffice()
    },
    destroy() {
      if (this.docEditor) {
        if(this.mode === 'edit'){
          this.docEditor.requestClose()
        }
        this.docEditor.destroyEditor()
        this.docEditor = null
      }
      const container = document.getElementById(this.containerId)
      if (container) container.innerHTML = ''
    },
    downloadFile() {
      if (this.fileUrl) {
        window.open(this.fileUrl, '_blank')
      } else {
        this.$message && this.$message.error('下载链接无效')
      }
    }
  },
  beforeDestroy() {
    this.destroy()
  }
}
</script>

<style scoped>
.onlyoffice-wrapper {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 4px;
  overflow: hidden;
}

.onlyoffice-content {
  flex: 1;
  overflow: hidden;
  position: relative;
  width: 100% !important;
  height: 100% !important;
}
</style>
